import { useTranslation } from 'react-i18next';

/**
 * Gets timer text color.
 *
 * @param {boolean} isCritical - The is critical.
 * @param {boolean} isWarning - The is warning.
 *
 * @returns {string} The result of getTimerTextColor.
 */
function getTimerTextColor(isCritical: boolean, isWarning: boolean): string {
  if (isCritical) return 'timer-badge__text--urgent timer-badge__text--running';
  if (isWarning) return 'timer-badge__text--urgent';
  return '';
}

/**
 * Formats time.
 *
 * @param {number} totalSeconds - The total seconds.
 *
 * @returns {string} The result of formatTime.
 */
function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

interface SessionTimerBarProps {
  sessionTimerDisplay: number;
  sessionTimerSeconds: number;
  sessionTimerWarning: boolean;
  sessionTimerCritical: boolean;
  isDemo?: boolean;
  isLoading?: boolean;
}

/**
 * Session timer bar.
 *
 * @param {SessionTimerBarProps} props - Component props.
 * @param {number} props.sessionTimerDisplay - The session timer display.
 * @param {number} props.sessionTimerSeconds - The session timer seconds.
 * @param {boolean} props.sessionTimerWarning - The session timer warning.
 * @param {boolean} props.sessionTimerCritical - The session timer critical.
 * @param {boolean | undefined} [props.isDemo] - The is demo.
 * @param {boolean | undefined} [props.isLoading] - Whether the timer has not yet started.
 *
 * @returns {JSX.Element} The rendered element.
 */
export function SessionTimerBar({
  sessionTimerDisplay,
  sessionTimerWarning,
  sessionTimerCritical,
  isDemo = false,
  isLoading = false,
}: SessionTimerBarProps) {
  const { t } = useTranslation();
  const textColor = getTimerTextColor(sessionTimerCritical, sessionTimerWarning);
  const formattedTime = formatTime(sessionTimerDisplay);

  return (
    <div className="gameplay-header-inner">
      <div className="timer-badge">
        {isDemo ? (
          <span className="timer-badge__content">
            <span className="timer-badge__text">
              <span className="timer-badge__value">{t('game.demo')}</span>
            </span>
          </span>
        ) : isLoading ? (
          <span className="timer-badge__content">
            <span className="flex items-center justify-center gap-1.5">
              {Array.from({ length: 3 }).map((_, i) => (
                <span
                  key={i}
                  className="h-1.5 w-1.5 rounded-full bg-white/70 animate-pulse"
                  style={{ animationDelay: `${(i * 0.2).toString()}s` }}
                />
              ))}
            </span>
          </span>
        ) : (
          <span
            className={`timer-badge__content timer-badge__text flex items-center gap-[3px] ${textColor}`}
          >
            <svg
              className="timer-badge__icon"
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="15"
              viewBox="0 0 13 15"
              fill="none"
              data-locator-target="vscode"
            >
              <path
                d="M6.44767 5.78274V8.44147L8.10938 9.4385M6.44767 2.79166C3.32736 2.79166 0.797852 5.32117 0.797852 8.44147C0.797852 11.5618 3.32736 14.0913 6.44767 14.0913C9.56797 14.0913 12.0975 11.5618 12.0975 8.44147C12.0975 5.32117 9.56797 2.79166 6.44767 2.79166ZM6.44767 2.79166V0.797607M5.1183 0.797607H7.77703M11.9838 3.18518L10.9868 2.18815L11.4853 2.68667M0.911526 3.18518L1.90855 2.18815L1.41004 2.68667"
                stroke="currentColor"
                strokeWidth="1.59524"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="timer-badge__value timer-badge__value--clock">{formattedTime}</span>
          </span>
        )}
      </div>
    </div>
  );
}
